import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { DropdownButton, MenuItem } from 'ui';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TwoOptionModal from 'projects/components/TwoOptionModal';
import TranscriptMenuItem from 'projects/components/transcript_nav/TranscriptMenuItem';
import { useDeleteTranscript } from 'hooks/transcripts/mutations';
import { useExportTranscriptMutation } from 'hooks/exports/mutations';
import ColorConstants from 'constants/ColorConstants';

function DashboardRowMenu({ projectID, transcriptId, canCompare, canDelete }) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const history = useHistory();
  const deleteTranscript = useDeleteTranscript(projectID);
  // Same mechanisms the transcript header uses: .txt is a client-side blob of
  // the transcript body; the coded .pdf is the print page's browser print
  // dialog (which self-gates on view-only / subscription).
  const exportTranscript = useExportTranscriptMutation();

  const onConfirmDelete = () => {
    setConfirmingDelete(false);
    deleteTranscript.mutate(transcriptId);
  };

  return (
    <>
      <TwoOptionModal
        show={confirmingDelete}
        title="Are you sure you want to delete this transcript?"
        onNo={() => setConfirmingDelete(false)}
        onYes={onConfirmDelete}
        noText="No, Cancel"
        yesText="Yes, Delete"
      >
        This will permanently delete the transcript and all of its coding.
      </TwoOptionModal>

      <DropdownButton
        variant="link"
        noCaret
        pullRight
        title={<MoreVertIcon data-testid="MoreVertIcon" style={{ fontSize: 16, color: ColorConstants.GRAY_400 }} />}
        id={`transcript-row-menu-${transcriptId}`}
        style={{ padding: 0, margin: 0 }}
      >
        <MenuItem
          eventKey="1"
          onClick={() => history.push(`/transcripts/${transcriptId}/user/me`)}
        >
          <TranscriptMenuItem description="Hide how other people coded.">
            Code Transcript in "Coded by Me" Mode
          </TranscriptMenuItem>
        </MenuItem>
        {canCompare && <MenuItem divider />}
        {canCompare && (
          <MenuItem
            eventKey="2"
            onClick={() => history.push(`/transcripts/${transcriptId}/compare`)}
          >
            <TranscriptMenuItem description="Compare how people coded.">
              See Coding Comparison
            </TranscriptMenuItem>
          </MenuItem>
        )}
        <MenuItem divider />
        <MenuItem
          eventKey="3"
          onClick={() => history.push(`/transcripts/${transcriptId}/demographics`)}
        >
          <TranscriptMenuItem description="Assign descriptors to transcripts.">
            See Participant Information
          </TranscriptMenuItem>
        </MenuItem>

        <MenuItem divider />
        <MenuItem eventKey="4" onClick={() => exportTranscript.mutate(transcriptId)}>
          <TranscriptMenuItem description="Plain text, no formatting.">
            Export Transcript (.txt)
          </TranscriptMenuItem>
        </MenuItem>
        <MenuItem divider />
        <MenuItem
          eventKey="5"
          onClick={() => window.open(`/transcripts/${transcriptId}/print`, '_blank')}
        >
          <TranscriptMenuItem description="Includes all coded highlights and tags.">
            Export Transcript with Codes (.pdf)
          </TranscriptMenuItem>
        </MenuItem>

        {canDelete && <MenuItem divider />}
        {canDelete && (
          <MenuItem eventKey="6" onClick={() => setConfirmingDelete(true)}>
            <span style={{ color: ColorConstants.DANGER_RED }}>Delete Transcript</span>
          </MenuItem>
        )}
      </DropdownButton>
    </>
  );
}

export default DashboardRowMenu;
